/**
 * @file Explosive component: the blast an entity leaves behind.
 *
 * Attaching it is the whole contract — {@link ../systems/ExplosionSystem.js}
 * reads this and does the rest. Any future entity (barrels, grenades, boss
 * cores) becomes explosive by gaining the component.
 *
 * Two triggers exist. An entity with health detonates when it dies, which is
 * what the exploder enemies use. A projectile has no health, so it opts into
 * `detonateOnDestroy` and goes off however it ends: wall, target, range or
 * timeout all funnel through the same destruction.
 */

import { Component } from '../ecs/Component.js';

/**
 * Why a blast happened. Only ever read by debug output, but naming it keeps the
 * three very different endings of a missile distinguishable.
 * @readonly
 * @enum {string}
 */
export const DetonationCause = Object.freeze({
  DEATH: 'death',
  WALL: 'wall',
  ENEMY: 'enemy',
  RANGE: 'range',
  TIMEOUT: 'timeout'
});

export class Explosive extends Component {
  /**
   * @param {object} options Blast setup.
   * @param {number} options.radius Blast radius in world units.
   * @param {number} options.damage Damage at the centre; falls off to zero at the edge.
   * @param {number} [options.mask] Collision layers the blast may damage.
   * @param {number} [options.ownerId] Entity id that fired it, for self-damage.
   * @param {number} [options.selfDamageMultiplier] Fraction of the blast the owner takes.
   * @param {number} [options.shake] Camera trauma override; 0 uses the config default.
   * @param {boolean} [options.detonateOnDestroy] Detonate when the entity is
   *   destroyed rather than only when it dies.
   */
  constructor({
    radius,
    damage,
    mask = 0,
    ownerId = 0,
    selfDamageMultiplier = 1,
    shake = 0,
    detonateOnDestroy = false
  }) {
    super();
    /** @type {number} */
    this.radius = radius;
    /** @type {number} */
    this.damage = damage;
    /** @type {number} Layers hurt by the blast; 0 means "anything with health". */
    this.mask = mask;
    /** @type {number} Entity id of whoever fired it; 0 when nobody owns it. */
    this.ownerId = ownerId;
    /** @type {number} Damage scale applied to the owner only. */
    this.selfDamageMultiplier = selfDamageMultiplier;
    /** @type {number} Camera trauma; 0 falls back to the config default. */
    this.shake = shake;
    /** @type {boolean} Whether destruction alone is enough to set it off. */
    this.detonateOnDestroy = detonateOnDestroy;

    /** @type {boolean} Latch, so one entity can never blow up twice. */
    this.detonated = false;
    /** @type {string} Populated on detonation; see {@link DetonationCause}. */
    this.cause = '';
    /**
     * Exact impact point, recorded by the collision system. A swept projectile
     * ends the frame a little past whatever stopped it, so without this the
     * blast would go off inside the wall it hit.
     * @type {boolean}
     */
    this.hasImpact = false;
    /** @type {number} */
    this.impactX = 0;
    /** @type {number} */
    this.impactZ = 0;
  }

  /** @returns {void} */
  reset() {
    this.detonated = false;
    this.cause = '';
    this.hasImpact = false;
  }
}
